import React, { useMemo, useState } from "react";
import { Box, Button, Checkbox, Divider, FormControlLabel, MenuItem, Stack, TextField, Typography } from "@mui/material";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import {
  useGetRolesQuery, useGetPermissionsQuery, useCreateRoleMutation,
  useUpdateRoleMutation, useDeleteRoleMutation,
} from "../../../Api/platformApi";
import {
  AppPage, PageHeader, FilterBar, SearchBox, DataTable, StatusBadge,
  ActionButtons, ActionButton, SlidePanel, ConfirmDialog, FormCard, type DataTableColumn,
} from "../../../Components/ui";
import { NAVY } from "../../../theme/tokens";

const Roles: React.FC = () => {
  const { data: rolesData, isLoading } = useGetRolesQuery();
  const { data: permsData } = useGetPermissionsQuery();
  const [createRole] = useCreateRoleMutation();
  const [updateRole] = useUpdateRoleMutation();
  const [deleteRole] = useDeleteRoleMutation();

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const roles = rolesData?.data ?? [];
  const grouped = permsData?.grouped ?? {};

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter((r: any) => r.name?.toLowerCase().includes(q));
  }, [roles, search]);

  const openCreate = () => { setEditId(null); setName(""); setSelected([]); setOpen(true); };
  const openEdit = (r: any) => {
    setEditId(r.id); setName(r.name);
    setSelected((r.permissions ?? []).map((p: any) => p.name));
    setOpen(true);
  };

  const toggle = (perm: string) =>
    setSelected((s) => s.includes(perm) ? s.filter((x) => x !== perm) : [...s, perm]);

  const save = async () => {
    const body = { name, permissions: selected };
    if (editId) await updateRole({ id: editId, body }).unwrap();
    else await createRole(body).unwrap();
    setOpen(false);
  };

  const columns: DataTableColumn<any>[] = [
    {
      id: "name", label: "Role",
      render: (r) => (
        <Box>
          <Typography fontSize={14} fontWeight={700} color={NAVY}>{r.name}</Typography>
        </Box>
      ),
    },
    {
      id: "system_role", label: "Type",
      render: (r) => (
        <StatusBadge
          status={r.system_role ? "system" : "custom"}
          tone={r.system_role ? "info" : "neutral"}
        />
      ),
    },
    {
      id: "permissions", label: "Permissions",
      render: (r) => (
        <Typography fontSize={13} color="text.secondary">
          {(r.permissions ?? []).length} permissions
        </Typography>
      ),
    },
  ];

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<ShieldRoundedIcon />}
          title="Roles &amp; Permissions"
          subtitle="Control what each role can access on the platform"
          actions={
            <ActionButton startIcon={<AddRoundedIcon />} onClick={openCreate}>
              Add Role
            </ActionButton>
          }
        />
      }
    >
      <FilterBar right={<Typography fontSize={13} color="text.secondary">{filtered.length} roles</Typography>}>
        <SearchBox value={search} onChange={setSearch} placeholder="Search roles…" />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={isLoading}
        actions={(r) =>
          !r.system_role ? (
            <ActionButtons
              onEdit={() => openEdit(r)}
              onDelete={() => setConfirmId(r.id)}
            />
          ) : null
        }
        empty={{
          icon: <ShieldRoundedIcon />,
          title: "No roles yet",
          message: "Create a role and assign permissions to control what members can do.",
          action: <ActionButton startIcon={<AddRoundedIcon />} onClick={openCreate}>Add Role</ActionButton>,
        }}
      />

      <SlidePanel
        open={open}
        onClose={() => setOpen(false)}
        title={editId ? "Edit Role" : "Add Role"}
        subtitle={editId ? "Update role name and permissions" : "Create a new role with custom permissions"}
        icon={<ShieldRoundedIcon />}
        width={560}
        footer={
          <>
            <Button variant="outlined" onClick={() => setOpen(false)} sx={{ borderRadius: "10px" }}>Cancel</Button>
            <ActionButton onClick={save} disabled={!name}>
              {editId ? "Update Role" : "Save Role"}
            </ActionButton>
          </>
        }
      >
        <Stack spacing={2}>
          <TextField
            label="Role Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          {Object.keys(grouped).map((module) => (
            <FormCard key={module} title={module.charAt(0).toUpperCase() + module.slice(1)}>
              <Stack direction="row" flexWrap="wrap">
                {grouped[module].map((p: any) => (
                  <FormControlLabel
                    key={p.name}
                    sx={{ width: 220 }}
                    control={<Checkbox checked={selected.includes(p.name)} onChange={() => toggle(p.name)} size="small" />}
                    label={<Typography fontSize={13}>{p.action}</Typography>}
                  />
                ))}
              </Stack>
            </FormCard>
          ))}
        </Stack>
      </SlidePanel>

      <ConfirmDialog
        open={confirmId !== null}
        title="Delete role?"
        message="This permanently removes the role. Members assigned to it will lose their permissions."
        confirmText="Delete"
        danger
        onClose={() => setConfirmId(null)}
        onConfirm={async () => { if (confirmId !== null) await deleteRole(confirmId); setConfirmId(null); }}
      />
    </AppPage>
  );
};

export default Roles;
